import { useMemo, useRef } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { parseInternalArticleLink } from "@productlane/lib/src/help-center/internal-links"
import { queries } from "@productlane/zero/queries"

import { ArticleLinkPills } from "@/components/editor/extensions/article-link-pills"
import { getPortalBaseUrl } from "@/lib/portal-base-url"
import { useZeroWorkspace } from "@/lib/zero"

interface ArticleLinkTarget {
	icon: string | null
}

// Renders links to another article in this help center as a pill, matching how
// the portal renders them. Keyed by id and urlName, since "@" inserts the
// id-based href while hand-written links use the urlName.
export function useArticleLinkPills() {
	const workspace = useZeroWorkspace()
	const [articles] = useQuery(queries.helpCenterArticles())

	const targets = useMemo(() => {
		const map: Record<string, ArticleLinkTarget> = {}
		for (const article of articles) {
			if (article.isDeleted || article.archived) continue
			map[article.id] = { icon: article.icon }
			if (article.urlName) map[article.urlName] = { icon: article.icon }
		}
		return map
	}, [articles])

	const hosts = useMemo(() => {
		const portalUrl = getPortalBaseUrl(
			workspace
				? {
						domain: workspace.domain,
						customDomain: workspace.customDomain ?? null,
					}
				: null,
		)
		return [new URL(portalUrl).host]
	}, [workspace])

	// The editor is built once, so the plugin reads the latest data through refs
	// instead of being reconfigured whenever an article changes.
	const latest = useRef({ targets, hosts })
	latest.current = { targets, hosts }

	return useMemo(
		() =>
			ArticleLinkPills.configure({
				resolveArticle: (href) => {
					const link = parseInternalArticleLink(href, {
						hosts: latest.current.hosts,
					})
					return link ? (latest.current.targets[link.segment] ?? null) : null
				},
			}),
		[],
	)
}
